import React, { useState, useEffect } from 'react';
import { Palette } from 'lucide-react';

const themes = {
  current: {
    name: 'Current (Purple & Orange)',
    primary: '#7C3AED',
    accent: '#F59E0B',
    dark: '#1A0F2E',
  },
  techBlue: {
    name: 'Modern Tech Blue',
    primary: '#2563EB',
    accent: '#10B981',
    dark: '#0F172A',
  },
  luxuryNavy: {
    name: 'Luxury Navy & Gold',
    primary: '#1E40AF',
    accent: '#F59E0B',
    dark: '#0C1E3E',
  },
  boldTeal: {
    name: 'Bold Teal & Coral',
    primary: '#0D9488',
    accent: '#F97316',
    dark: '#134E4A',
  },
  cleanIndigo: {
    name: 'Clean Indigo & Cyan',
    primary: '#4F46E5',
    accent: '#06B6D4',
    dark: '#1E1B4B',
  },
  warmSlate: {
    name: 'Warm Slate & Amber',
    primary: '#475569',
    accent: '#F59E0B',
    dark: '#0F172A',
  },
  boldRose: {
    name: 'Bold Rose & Purple',
    primary: '#E11D48',
    accent: '#A855F7',
    dark: '#1F1729',
  },
};

export const ThemeSwitcher = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('current');

  useEffect(() => {
    const theme = themes[currentTheme];
    document.documentElement.style.setProperty('--color-primary', theme.primary);
    document.documentElement.style.setProperty('--color-accent', theme.accent);
    document.documentElement.style.setProperty('--color-dark', theme.dark);
  }, [currentTheme]);

  const handleThemeChange = (themeKey) => {
    setCurrentTheme(themeKey);
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Theme Switcher Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white shadow-lg rounded-full p-4 hover:shadow-xl transition-all duration-200 border-2 border-gray-200 min-w-[48px] min-h-[48px] flex items-center justify-center"
        title="Change Theme"
        aria-label={isOpen ? "Close theme selector" : "Open theme selector"}
        aria-expanded={isOpen}
      >
        <Palette className="w-6 h-6 text-gray-700" />
      </button>

      {/* Theme Options Panel */}
      {isOpen && (
        <div className="absolute bottom-20 right-0 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 w-80 max-h-96 overflow-y-auto">
          <h3 className="text-lg font-bold mb-3 text-gray-900">Choose Theme</h3>
          <div className="space-y-2">
            {Object.entries(themes).map(([key, theme]) => (
              <button
                key={key}
                onClick={() => handleThemeChange(key)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 ${
                  currentTheme === key
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm text-gray-900">{theme.name}</span>
                  {currentTheme === key && (
                    <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded">Active</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <div
                    className="w-8 h-8 rounded border border-gray-300"
                    style={{ backgroundColor: theme.primary }}
                    title="Primary"
                  />
                  <div
                    className="w-8 h-8 rounded border border-gray-300"
                    style={{ backgroundColor: theme.accent }}
                    title="Accent"
                  />
                  <div
                    className="w-8 h-8 rounded border border-gray-300"
                    style={{ backgroundColor: theme.dark }}
                    title="Dark"
                  />
                </div>
              </button>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Preview themes in real-time. Changes are temporary and won't be saved.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
